import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle, History, Facebook, Chrome, Music, BarChart3, Search, Users } from 'lucide-react';
import { AuthContext } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORMS = [
  { id: 'auto', name: 'Detectar automáticamente', icon: Upload, color: '#d946ef', formats: '.csv, .xlsx, .zip' },
  { id: 'meta', name: 'Meta Ads', icon: Facebook, color: '#0668E1', formats: '.csv' },
  { id: 'google', name: 'Google Ads', icon: Chrome, color: '#EA4335', formats: '.csv' },
  { id: 'tiktok', name: 'TikTok Ads', icon: Music, color: '#000000', formats: '.xlsx' },
  { id: 'ga4', name: 'Google Analytics 4', icon: BarChart3, color: '#F9AB00', formats: '.csv' },
  { id: 'gsc', name: 'Search Console', icon: Search, color: '#4285F4', formats: '.zip' }
];

export default function UploadCSV() {
  const { token } = useContext(AuthContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('auto');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statusRes, historyRes, clientsRes] = await Promise.all([
        axios.get(`${API}/subscription/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/uploads/history`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setSubscriptionStatus(statusRes.data);
      setUploadHistory(historyRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const getAcceptedFormats = () => {
    const platform = PLATFORMS.find(p => p.id === selectedPlatform);
    if (selectedPlatform === 'auto') {
      return {
        'text/csv': ['.csv'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/zip': ['.zip']
      };
    }
    if (platform?.formats.includes('.xlsx')) {
      return { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] };
    }
    if (platform?.formats.includes('.zip')) {
      return { 'application/zip': ['.zip'] };
    }
    return { 'text/csv': ['.csv'] };
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    
    if (!subscriptionStatus?.can_upload) {
      toast.error(`Límite alcanzado: ${subscriptionStatus?.usage?.uploads}/${subscriptionStatus?.limits?.uploads} uploads. Mejora tu plan.`);
      navigate('/pricing');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('platform', selectedPlatform);
      if (selectedClient) {
        formData.append('client_id', selectedClient);
      }

      const response = await axios.post(`${API}/uploads/csv`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`✓ ${response.data.message}`);
      fetchData();
      
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al procesar archivo';
      toast.error(errorMsg);
      
      if (error.response?.status === 403) {
        setTimeout(() => navigate('/pricing'), 2000);
      }
    } finally {
      setUploading(false);
    }
  }, [subscriptionStatus, token, navigate, selectedPlatform, selectedClient]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: getAcceptedFormats(),
    multiple: false,
    disabled: uploading || !subscriptionStatus?.can_upload
  });

  const canUpload = subscriptionStatus?.can_upload;
  const usageText = subscriptionStatus 
    ? `${subscriptionStatus.usage.uploads}/${subscriptionStatus.limits.uploads === 999999 ? '∞' : subscriptionStatus.limits.uploads}`
    : '0/0';

  const currentPlatform = PLATFORMS.find(p => p.id === selectedPlatform);

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="upload-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className={`font-heading font-bold text-4xl mb-2 ${theme.text}`}>Subir Datos</h1>
          <p className={theme.textSecondary}>Importa tus campañas de las 5 plataformas soportadas</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Usage Info */}
            {subscriptionStatus && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className={`${theme.glassCard} p-4 rounded-xl ${!canUpload ? 'border-warning' : ''}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-brand-meta" />
                    <span className={`text-sm font-medium ${theme.text}`}>
                      Uploads este mes: <span className="font-bold">{usageText}</span>
                    </span>
                  </div>
                  {!canUpload && (
                    <Button size="sm" onClick={() => navigate('/pricing')}
                      className="bg-warning text-black hover:bg-warning/90 rounded-full">
                      Mejorar Plan
                    </Button>
                  )}
                </div>
              </motion.div>
            )}

            {/* Client & Platform Selection */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className={`${theme.glassCard} p-6 rounded-xl`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Client Selector */}
                <div>
                  <label className={`block text-sm font-medium mb-2 flex items-center ${theme.text}`}>
                    <Users className="w-4 h-4 mr-2" />
                    Cliente (opcional)
                  </label>
                  <select
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    className={`w-full ${theme.input} rounded-lg p-3`}
                    data-testid="client-selector"
                  >
                    <option value="">Sin cliente asignado</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                  {clients.length === 0 && (
                    <p className={`text-xs ${theme.textSecondary} mt-2`}>
                      <a href="/clients" className="text-ai-glow hover:underline">Crear un cliente</a> para organizar tus campañas
                    </p>
                  )}
                </div>

                {/* Platform Selector */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme.text}`}>Plataforma</label>
                  <select
                    value={selectedPlatform}
                    onChange={(e) => setSelectedPlatform(e.target.value)}
                    className={`w-full ${theme.input} rounded-lg p-3`}
                    data-testid="platform-selector"
                  >
                    {PLATFORMS.map(platform => (
                      <option key={platform.id} value={platform.id}>
                        {platform.name} ({platform.formats})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </motion.div>

            {/* Platform Cards */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="grid grid-cols-5 gap-2">
              {PLATFORMS.filter(p => p.id !== 'auto').map((platform) => {
                const Icon = platform.icon;
                const isSelected = selectedPlatform === platform.id;
                return (
                  <button
                    key={platform.id}
                    onClick={() => setSelectedPlatform(platform.id)}
                    className={`p-3 rounded-xl border text-center transition-all ${
                      isSelected 
                        ? `border-2 ${theme.isDark ? 'bg-white/10' : 'bg-gray-100'}` 
                        : `${theme.border} ${theme.cardHover}`
                    }`}
                    style={{ borderColor: isSelected ? platform.color : undefined }}
                    data-testid={`platform-btn-${platform.id}`}
                  >
                    <Icon className="w-6 h-6 mx-auto mb-1" style={{ color: platform.color }} />
                    <p className={`text-xs truncate ${theme.text}`}>{platform.name.split(' ')[0]}</p>
                  </button>
                );
              })}
            </motion.div>

            {/* Dropzone */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <div
                {...getRootProps()}
                className={`${theme.glassCard} p-12 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                  isDragActive 
                    ? 'border-brand-meta bg-brand-meta/10' 
                    : !canUpload
                    ? `${theme.textSecondary} opacity-50 cursor-not-allowed`
                    : `${theme.isDark ? 'border-white/20 hover:border-brand-meta hover:bg-white/5' : 'border-gray-300 hover:border-brand-meta hover:bg-gray-100'}`
                }`}
                style={{ borderColor: isDragActive ? currentPlatform?.color : undefined }}
                data-testid="dropzone"
              >
                <input {...getInputProps()} />
                
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full opacity-30 flex items-center justify-center"
                    style={{ backgroundColor: currentPlatform?.color + '30' }}>
                    {currentPlatform && <currentPlatform.icon className="w-10 h-10" style={{ color: currentPlatform.color }} />}
                  </div>
                  
                  {uploading ? (
                    <>
                      <p className={`text-xl font-semibold mb-2 ${theme.text}`}>Procesando archivo...</p>
                      <p className={`${theme.textSecondary} text-sm`}>Esto puede tomar unos segundos</p>
                    </>
                  ) : !canUpload ? (
                    <>
                      <p className="text-xl font-semibold mb-2 text-warning">Límite alcanzado</p>
                      <p className={`${theme.textSecondary} text-sm mb-4`}>Has usado todos tus uploads este mes</p>
                      <Button onClick={() => navigate('/pricing')} className="bg-warning text-black rounded-full">
                        Mejorar Plan
                      </Button>
                    </>
                  ) : isDragActive ? (
                    <>
                      <p className={`text-xl font-semibold mb-2 ${theme.text}`}>Suelta el archivo aquí</p>
                      <p className={`${theme.textSecondary} text-sm`}>Procesaremos tu archivo automáticamente</p>
                    </>
                  ) : (
                    <>
                      <p className={`text-xl font-semibold mb-2 ${theme.text}`}>Arrastra tu archivo aquí</p>
                      <p className={`${theme.textSecondary} text-sm mb-4`}>o haz click para seleccionar</p>
                      <p className={`text-xs ${theme.textSecondary}`}>
                        Formato: <span className={`${theme.text} font-medium`}>{currentPlatform?.formats}</span>
                      </p>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar - Instructions & History */}
          <div className="space-y-6">
            {/* Platform Instructions */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
              className={`${theme.glassCard} p-6 rounded-xl`}>
              <h3 className={`font-medium mb-4 flex items-center ${theme.text}`}>
                <AlertCircle className="w-5 h-5 mr-2 text-ai-glow" />
                Instrucciones
              </h3>
              <div className={`space-y-4 text-sm ${theme.textSecondary}`}>
                <div className={`p-3 rounded-lg ${theme.isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <p className={`font-medium ${theme.text} mb-1`}>Meta Ads (.csv)</p>
                  <p>Exporta desde Ads Manager → Informes → Exportar</p>
                </div>
                <div className={`p-3 rounded-lg ${theme.isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <p className={`font-medium ${theme.text} mb-1`}>Google Ads (.csv)</p>
                  <p>Informes → Descargar → CSV</p>
                </div>
                <div className={`p-3 rounded-lg ${theme.isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <p className={`font-medium ${theme.text} mb-1`}>TikTok Ads (.xlsx)</p>
                  <p>Campaigns → Export → Excel</p>
                </div>
                <div className={`p-3 rounded-lg ${theme.isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <p className={`font-medium ${theme.text} mb-1`}>GA4 (.csv)</p>
                  <p>Explorar → Exportar → CSV</p>
                </div>
                <div className={`p-3 rounded-lg ${theme.isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <p className={`font-medium ${theme.text} mb-1`}>Search Console (.zip)</p>
                  <p>Rendimiento → Exportar → Descargar ZIP</p>
                </div>
              </div>
            </motion.div>

            {/* Upload History */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
              className={`${theme.glassCard} p-6 rounded-xl`}>
              <h3 className={`font-medium mb-4 flex items-center ${theme.text}`}>
                <History className="w-5 h-5 mr-2 text-brand-meta" />
                Historial Reciente
              </h3>
              
              {uploadHistory.length === 0 ? (
                <p className={`${theme.textSecondary} text-sm`}>No hay archivos subidos aún</p>
              ) : (
                <div className="space-y-3">
                  {uploadHistory.slice(0, 5).map((upload, index) => (
                    <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${theme.isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                      <div className="flex items-center space-x-3">
                        {upload.status === 'completed' ? (
                          <CheckCircle className="w-4 h-4 text-success" />
                        ) : upload.status === 'failed' ? (
                          <XCircle className="w-4 h-4 text-error" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-warning" />
                        )}
                        <div>
                          <p className={`text-sm font-medium truncate max-w-[150px] ${theme.text}`}>{upload.filename}</p>
                          <p className={`text-xs ${theme.textSecondary}`}>{upload.platform} • {upload.records || 0} registros</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
